/**
 * Read from IndexedDB, then from the network (CLAUDE.md Rule 4).
 *
 * Written once because every read-heavy screen needs the same four things and each of
 * them has a way to get it subtly wrong:
 *
 * - render the cached answer immediately, so a plant room with no signal still shows work
 *   and a 3G handset shows it seconds before the endpoint replies;
 * - refresh from the network and store what comes back;
 * - on a failed request fall back to the cache, and only report an error when there is
 *   nothing cached to fall back to;
 * - never let a slow cache read land on top of a fresher network answer.
 *
 * The fallback deliberately triggers on the request failing rather than on
 * `navigator.onLine`, which reports true on captive portals and on wifi that is connected
 * to nothing — the two situations most like a hotel basement.
 */
export interface CacheFirstOptions<T> {
  /** The cached value, or null when this device has never stored one. */
  readCache: () => Promise<T | null>;
  /** The network read. */
  load: () => Promise<T>;
  /** Stores a fresh network answer. Failures here are not worth showing anyone. */
  writeCache: (value: T) => Promise<void>;
  /**
   * Called for each answer, cache first then network. `source` matters wherever a screen
   * acts on the value rather than just rendering it: a cached list may be a subset of the
   * truth, so anything irreversible should wait for `'network'`.
   */
  onValue: (value: T, source: 'cache' | 'network') => void;
  /** Only when the network failed *and* the cache was empty. */
  onError: (error: Error) => void;
}

/**
 * Starts a cache-first read. Returns a cancel function — call it when the component
 * unmounts or the query changes, or a late answer will set state on a dead screen.
 */
export function cacheFirst<T>({
  readCache,
  load,
  writeCache,
  onValue,
  onError,
}: CacheFirstOptions<T>): () => void {
  let cancelled = false;
  // Set as soon as the network answers, so the cache read below cannot overwrite it.
  let fresh = false;
  // Both paths below read the cache — the racing read, and the fallback after a failed
  // request. Without this latch each one delivers, and an offline load renders identical
  // rows twice: a second full re-render of the queue or the picker, on the handset least
  // able to afford it. Whichever gets there first wins; the other stays quiet.
  let servedFromCache = false;

  void readCache()
    .then((cached) => {
      if (cancelled || fresh || servedFromCache || cached === null) return;
      servedFromCache = true;
      onValue(cached, 'cache');
    })
    // A cache miss is not a failure worth reporting: the request is still in flight and
    // owns the error message if it also fails.
    .catch(() => undefined);

  void load()
    .then(async (value) => {
      fresh = true;
      await writeCache(value).catch(() => undefined);
      if (!cancelled) onValue(value, 'network');
    })
    .catch(async (caught: unknown) => {
      if (servedFromCache) return;

      const cached = await readCache().catch(() => null);
      // Re-checked after the await: the racing read may have delivered while this one
      // was in flight, which is the same double render arriving in the other order.
      if (cancelled || servedFromCache) return;

      if (cached !== null) {
        servedFromCache = true;
        onValue(cached, 'cache');
        return;
      }
      onError(caught instanceof Error ? caught : new Error(String(caught)));
    });

  return () => {
    cancelled = true;
  };
}
